import { useCallback, useEffect, useState } from 'react'
import { Button, Group, Stack, Table, Text, Textarea, TextInput } from '@mantine/core'
import { openConfirmModal } from '@mantine/modals'
import { showNotification } from '@mantine/notifications'
import { ActionButton, AppModal, PageCenterLoader } from '@/components/ui'
import { api, ApiError } from '@/lib/api'
import type { ProjectTemplateRow } from '@/types/api'

/** Список и редактирование шаблонов ответов по проекту (как вкладка «Шаблоны» в настройках проекта). */
export function ProjectTemplatesPanel({ projectId, canEdit }: { projectId: string; canEdit: boolean }) {
  const [rows, setRows] = useState<ProjectTemplateRow[]>([])
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState<ProjectTemplateRow | 'new' | null>(null)
  const [saving, setSaving] = useState(false)
  const [tplName, setTplName] = useState('')
  const [tplSubject, setTplSubject] = useState('')
  const [tplContent, setTplContent] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const list = await api.projectTemplates(projectId)
      setRows(Array.isArray(list) ? list : [])
    } catch {
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [projectId])

  useEffect(() => {
    void load()
  }, [load])

  const openNew = () => {
    setModal('new')
    setTplName('')
    setTplSubject('')
    setTplContent('')
  }

  const openEdit = (t: ProjectTemplateRow) => {
    setModal(t)
    setTplName(t.name)
    setTplSubject(t.subject ?? '')
    setTplContent(t.content ?? '')
  }

  const saveTpl = async () => {
    if (!canEdit) return
    const n = tplName.trim()
    if (!n || !tplContent.trim()) {
      showNotification({ title: 'Форма', message: 'Название и текст шаблона обязательны', color: 'yellow' })
      return
    }
    setSaving(true)
    try {
      if (modal === 'new') {
        await api.projectTemplateCreate(projectId, {
          name: n,
          content: tplContent,
          subject: tplSubject.trim() || null,
        })
      } else if (modal) {
        await api.projectTemplateUpdate(projectId, modal.id, {
          name: n,
          content: tplContent,
          subject: tplSubject.trim() || null,
        })
      }
      showNotification({ title: 'Сохранено', message: '', color: 'green' })
      setModal(null)
      await load()
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось сохранить',
        color: 'red',
      })
    } finally {
      setSaving(false)
    }
  }

  const del = (t: ProjectTemplateRow) => {
    if (!canEdit) return
    openConfirmModal({
      title: 'Удалить шаблон?',
      children: <Text size="sm">{t.name}</Text>,
      labels: { confirm: 'Удалить', cancel: 'Отмена' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await api.projectTemplateDelete(projectId, t.id)
          showNotification({ title: 'Удалено', message: '', color: 'green' })
          await load()
        } catch (e) {
          showNotification({
            title: 'Ошибка',
            message: e instanceof ApiError ? e.message : 'Не удалось удалить',
            color: 'red',
          })
        }
      },
    })
  }

  if (loading) return <PageCenterLoader label="Шаблоны" minHeight={100} />

  return (
    <Stack gap="md">
      {canEdit ? (
        <Button w="fit-content" size="sm" onClick={openNew}>
          Добавить шаблон
        </Button>
      ) : null}
      <Table striped>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Название</Table.Th>
            <Table.Th w={160} />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((t) => (
            <Table.Tr key={t.id}>
              <Table.Td>{t.name}</Table.Td>
              <Table.Td>
                {canEdit ? (
                  <Group gap="xs">
                    <Button size="compact-xs" variant="default" onClick={() => openEdit(t)}>
                      Изменить
                    </Button>
                    <Button size="compact-xs" color="red" variant="light" onClick={() => del(t)}>
                      Удалить
                    </Button>
                  </Group>
                ) : null}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      <AppModal
        opened={modal != null}
        onClose={() => setModal(null)}
        title={modal === 'new' ? 'Шаблон' : 'Шаблон'}
        size="lg"
        footer={
          <>
            <ActionButton action="cancel" onClick={() => setModal(null)} />
            <ActionButton action="save" loading={saving} onClick={() => void saveTpl()} />
          </>
        }
      >
        <Stack gap="sm">
          <TextInput label="Название" value={tplName} onChange={(e) => setTplName(e.target.value)} />
          <TextInput label="Тема (email)" value={tplSubject} onChange={(e) => setTplSubject(e.target.value)} />
          <Textarea label="Текст" value={tplContent} onChange={(e) => setTplContent(e.target.value)} minRows={6} />
        </Stack>
      </AppModal>
    </Stack>
  )
}
